import type { CommunityNote } from "../../api/community";

export type CommunitySort = "recommend" | "latest" | "hot" | "follow";

export const COMMUNITY_SORTS: { id: CommunitySort; label: string }[] = [
  { id: "recommend", label: "推荐" },
  { id: "latest", label: "最新" },
  { id: "hot", label: "最热" },
  { id: "follow", label: "关注" },
];

export function readSort(value: string | null): CommunitySort {
  return COMMUNITY_SORTS.some((item) => item.id === value) ? (value as CommunitySort) : "recommend";
}

export function formatCount(value: number) {
  if (value >= 10_000) return `${Math.round(value / 1000) / 10}万+`;
  if (value >= 1000) return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(value);
}

export function excerpt(content: string | null | undefined, length = 72) {
  const text = (content || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return "这篇笔记还没有正文";
  return text.length > length ? `${text.slice(0, length)}…` : text;
}

export function formatTime(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diff = Date.now() - date.getTime();
  const minute = 60 * 1000;
  if (diff < minute) return "刚刚";
  if (diff < 60 * minute) return `${Math.floor(diff / minute)} 分钟前`;
  if (diff < 24 * 60 * minute) return `${Math.floor(diff / (60 * minute))} 小时前`;
  if (diff < 7 * 24 * 60 * minute) return `${Math.floor(diff / (24 * 60 * minute))} 天前`;
  return new Intl.DateTimeFormat("zh-CN", { month: "short", day: "numeric" }).format(date);
}

export function formatDateDot(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}.${month}.${day}`;
}

export function authorInitial(name?: string | null) {
  const value = name?.trim() || "设";
  return value.slice(0, 1).toUpperCase();
}

export function noteSubtitle(content?: string | null) {
  const text = (content || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return "探索材质、形态与空间的情绪表达。";
  return text.length > 48 ? `${text.slice(0, 48)}…` : text;
}

export function noteTags(note: CommunityNote) {
  const tags = new Set<string>();
  if (note.topic) tags.add(note.topic.replace(/^#/, "").trim());
  const matches = (note.content || "").match(/#[\u4e00-\u9fa5A-Za-z0-9_]+/g) || [];
  for (const match of matches) {
    const tag = match.slice(1).trim();
    if (tag) tags.add(tag);
  }
  return [...tags].slice(0, 4);
}

export function noteParagraphs(content?: string | null) {
  const text = (content || "").trim();
  if (!text) return [] as string[];
  return text.split(/\n{2,}/).map((item) => item.trim()).filter(Boolean);
}

export function coverForNote(note: CommunityNote) {
  return note.coverUrl || note.imageUrls?.[0] || "";
}

/** 列表卡片优先用缩略图，没有时退回原图。 */
export function thumbForNote(note: CommunityNote) {
  return note.coverThumbUrl || coverForNote(note);
}

export function imagesForNote(note: CommunityNote) {
  const images = [note.coverUrl, ...(note.imageUrls ?? [])].filter((item): item is string => Boolean(item));
  return images.filter((item, index, list) => list.indexOf(item) === index);
}
